import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import Fetcher from '../util/Fetcher';
import withDynamoPagination from '../util/withDynamoPagination';
import noop from '../util/noop';
import t from '@instructure/quiz-i18n/es/format-message';
import { handleQuizSessionResponse } from './callHandlers';
import { OQAAT_NO_BACKTRACKING_NEXT_ITEM_FETCH_CALL, SESSION_ITEMS_FETCH_CALL, DO_NOTHING } from '@instructure/quiz-common';
import QuizSessionResult from '../records/QuizSessionResult';
import { addError } from '../actions/alerts';
var generateUrl = function generateUrl(pathParts) {
  return ['', 'api', 'quiz_sessions'].concat(_toConsumableArray(pathParts)).join('/');
};
var get = function get(url, responseCb) {
  var params = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var fetcherParams = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var markCallbacksComplete = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  var fetcher = new Fetcher(fetcherParams);
  var getCall = fetcher.get(url, params).then(responseCb);
  if (markCallbacksComplete) {
    return getCall.then(function () {
      return fetcher.markCallbacksComplete();
    });
  }
  return getCall;
};
var dynamoGet = function dynamoGet(url, responseCb) {
  var fetcherParams = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var markCallbacksComplete = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  var DynamoFetcher = withDynamoPagination(Fetcher);
  var DynamoFetcherGet = new DynamoFetcher(fetcherParams);
  var getCall = DynamoFetcherGet.paginatedGet(url).then(responseCb);
  if (markCallbacksComplete) {
    return getCall.then(function () {
      return DynamoFetcherGet.markCallbacksComplete();
    });
  }
  return getCall;
};
var post = function post(url, params, responseCb) {
  var fetcherParams = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var fetcher = new Fetcher(fetcherParams);
  return fetcher.post(url, params).then(responseCb);
};

// /////////////////////
//  GENERAL
// /////////////////////

export var fetchQuizSession = function fetchQuizSession(quizSessionId) {
  var responseCb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  var params = arguments.length > 2 ? arguments[2] : undefined;
  return get(generateUrl([quizSessionId]), responseCb, params);
};
export var fetchSessionItems = function fetchSessionItems(quizSessionId) {
  var responseCb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  return dynamoGet(generateUrl([quizSessionId, 'session_items']), responseCb, {
    callType: SESSION_ITEMS_FETCH_CALL
  });
};

// //////////////////////
// BEFORE TAKING
// //////////////////////
export var submitStudentAccessCode = function submitStudentAccessCode(quizSessionId, accessCode) {
  var responseCb = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  return post(generateUrl([quizSessionId, 'submit_student_access_code']), {
    body: JSON.stringify({
      accessCode: accessCode
    })
  }, responseCb, {});
};

// //////////////////////
// TAKING
// //////////////////////

export var takeQuizSession = function takeQuizSession(quizSessionId) {
  var responseCb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  // Avoid generating the error header; errors handled via catch
  return post(generateUrl([quizSessionId, 'take']), {}, responseCb, {
    onError: function onError() {
      return {
        type: DO_NOTHING
      };
    }
  });
};
export var fetchResumeData = function fetchResumeData(quizSessionId) {
  var responseCb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  return get(generateUrl([quizSessionId, 'session_item_responses', 'resume_data']), responseCb);
};

// This is too widely used to be updated to use the common pattern here, fine as is
export var pinSessionItem = function pinSessionItem(quizSessionId, sessionItemPosition) {
  return function (dispatch, getState) {
    var responseCb = function responseCb(quizSessionData) {
      return dispatch(handleQuizSessionResponse(quizSessionData));
    };
    var params = {
      body: JSON.stringify({
        position: sessionItemPosition
      })
    };
    return post(generateUrl([quizSessionId, 'toggle_session_item_pin']), params, responseCb);
  };
};
export var fetchTime = function fetchTime(quizSessionId) {
  var responseCb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  return get(generateUrl([quizSessionId, 'time']), responseCb);
};
export var recordTime = function recordTime(quizSessionId) {
  var responseCb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  return get(generateUrl([quizSessionId, 'record']), responseCb);
};

// /////////////////////
//  TAKING SUBMIT
// /////////////////////

var onSubmitError = function onSubmitError(responseCb, errorCb) {
  return function (error) {
    if (error.response && error.response.status === 422) {
      responseCb(); // responseCb is the page navigation to Results (comes from quiz_lti)
      return addError(t('This attempt was already submitted. Showing results from the prior submission.'));
    }
    if (errorCb) errorCb(error.response.status);
    return addError(t('Submission error. Please wait 10 seconds and try again.'));
  };
};
var onSingleResponseSubmitError = function onSingleResponseSubmitError(responseCb, errorCb) {
  return function (error) {
    if (error.response && error.response.status == 400) {
      errorCb(error.response.status);
      return addError(t('Submission error. If the problem persists try reloading the page.'));
    }
    return onSubmitError(responseCb)(error);
  };
};
export var singleResponseSubmit = function singleResponseSubmit(quizSessionId, userResponse) {
  var responseCb = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  var errorCb = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;
  // if the server returns a 204, don't try to JSON deserialize the
  // empty response (204 = No Content)
  var asyncResponseHandler = function asyncResponseHandler(resp) {
    return resp.status === 204 ? Promise.resolve(null) : resp.json();
  };
  return post(generateUrl([quizSessionId, 'session_item_responses', 'single_response_submit']), userResponse, responseCb, {
    asyncResponseHandler: asyncResponseHandler,
    callType: OQAAT_NO_BACKTRACKING_NEXT_ITEM_FETCH_CALL,
    onError: onSingleResponseSubmitError(responseCb, errorCb)
  })["catch"](function (e) {
    // tests don't like uncaught errors
  });
};
export var submitQuizSession = function submitQuizSession(quizSessionId, userResponses) {
  var responseCb = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  var errorCb = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;
  return post(generateUrl([quizSessionId, 'submit']), userResponses, responseCb, {
    onError: onSubmitError(responseCb, errorCb)
  })["catch"](function (e) {
    // tests don't like uncaught errors
  });
};

// ///////////////////////////////
//  POST TAKING (RESULTS/GRADING)
// ///////////////////////////////

export var fetchSessionItemResults = function fetchSessionItemResults(quizSessionId, quizSessionResultId) {
  var responseCb = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  return dynamoGet(generateUrl([quizSessionId, 'results', quizSessionResultId, 'session_item_results']), responseCb);
};
export var fetchQuizSessionResult = function fetchQuizSessionResult(quizSessionId, quizSessionResultId) {
  var responseCb = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  var fetcherKey = QuizSessionResult.fetcherTag(quizSessionResultId);
  return get(generateUrl([quizSessionId, 'results', quizSessionResultId]), responseCb, {}, {
    callType: fetcherKey
  }, true);
};